/**
 * Player Development Review engine.
 *
 * Surfaces players where xOVR has diverged meaningfully from OVR — signaling
 * that real-game performance is ahead of (or behind) the coach's scouted ratings.
 * The coach reviews each flagged player and either accepts the proposed attribute
 * nudge (updating Firestore) or skips it until next cycle.
 *
 * Rules (from product spec):
 *   • Gap threshold: ±4 OVR points between xOVR and OVR
 *   • Sample minimum: 4 games with plate appearances
 *   • Only the primary driver attribute moves, capped at ±1 per review cycle
 *   • Batting above → nudge contact up; batting below → nudge contact down
 *   • Pitching above → nudge control up (if walks-driven) or velocity up (K-driven)
 *   • Pitching below → nudge control down
 *
 * Season scoping: callers MUST pass games already filtered to the current season
 * (see currentSeasonGames in ./utils). Proposals are driven purely by current-season
 * data, so after a "Start New Season" rollover the review stays quiet until the
 * player has 4 games in the new season. Last season informs display, never a proposal.
 */

import { calculateOVR } from './utils';
import { calculateXOVR } from './xovr-engine';

export interface DevReviewProposal {
  playerId: string;
  playerName: string;
  playerNumber: string;
  primaryPosition: string;
  currentOVR: number;
  xOVR: number;
  gap: number; // xOVR - currentOVR, signed
  /** Which attribute bucket and key will change */
  attributeBucket: 'hitting' | 'pitching';
  attributeKey: string;
  attributeLabel: string;
  currentValue: number;
  proposedValue: number;
  /** Plain-English reason shown to the coach */
  reason: string;
  /** The stat line that drove the signal */
  statLine: string;
}

/** How many games with PA a player needs before they're eligible for review. */
const MIN_GAMES_WITH_PA = 4;
/** Minimum absolute OVR gap to surface a player. */
const MIN_GAP = 4;

/** Count games where a player had at least 1 plate appearance. */
function gamesWithPA(playerId: string, games: any[]): number {
  return games.filter(g =>
    g.status === 'completed' &&
    g.stats?.[playerId] &&
    (g.stats[playerId].pa ?? g.stats[playerId].ab ?? 0) > 0
  ).length;
}

/** Given a player and their stats, return the proposed attribute change or null. */
export function buildDevReviewProposal(
  player: any,
  games: any[]
): DevReviewProposal | null {
  if (!player.attributes) return null;

  // ── Sample size gate ────────────────────────────────────────────────────────
  const gameCount = gamesWithPA(player.id, games);
  if (gameCount < MIN_GAMES_WITH_PA) return null;

  // ── Compute OVR and xOVR ────────────────────────────────────────────────────
  const attrs = player.attributes;
  const pos = player.primaryPosition || '';
  const isPitcher = (attrs.pitching?.velocity || 0) > 0 || (attrs.pitching?.control || 0) > 0;
  const attrForOVR = isPitcher ? attrs : { ...attrs, pitching: { velocity: 0, control: 0 } };
  const scoutingOVR = calculateOVR(attrForOVR, pos);

  // CURRENT SEASON ONLY. A proposal changes a player's scouting attributes, so it
  // must be earned by what the coach has seen this season — never by last season's
  // numbers carried in `priorSeasonStats`. Passing null as the third argument means
  // the xOVR engine's prior-season fallback can't fire here. (Roster badges in
  // App.tsx still pass priorStats, so display stays populated after a rollover.)
  const currentStats = player.seasonSnapshot?.stats ?? player.stats ?? null;
  if (!currentStats) return null;

  const breakdown = calculateXOVR(scoutingOVR, currentStats, null);
  const xovr = breakdown.xovr;
  const gap = xovr - scoutingOVR;

  if (Math.abs(gap) < MIN_GAP) return null;

  // Already reviewed recently? Check the player's devReviewedAt timestamp.
  // (Skipped players store devReviewDismissedAt — we don't resurface them for 2 weeks.)
  const dismissedAt = player.devReviewDismissedAt;
  if (dismissedAt) {
    const daysSince = (Date.now() - new Date(dismissedAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince < 14) return null;
  }

  // ── Determine which attribute to nudge ─────────────────────────────────────
  const direction = gap > 0 ? 1 : -1;
  const stats = currentStats;
  const pa = stats?.pa ?? 0;
  const ip = stats?.ip ?? 0;
  const hasPitchingSignal = isPitcher && ip >= 1;

  let attributeBucket: 'hitting' | 'pitching';
  let attributeKey: string;
  let attributeLabel: string;
  let currentValue: number;
  let reason: string;
  let statLine: string;

  if (hasPitchingSignal && (!pa || breakdown.signalType === 'pitching')) {
    // Pitching signal: use control (walk-driven) or velocity (K-driven)
    attributeBucket = 'pitching';
    const walks = stats?.bb ?? stats?.bbAllowed ?? 0;
    const ks = stats?.soAllowed ?? stats?.k ?? 0;
    const whip = stats?.whip ?? 0;
    const era = stats?.era ?? 0;
    const usesControl = gap < 0 || (whip > 0 && whip > 1.4);
    attributeKey = usesControl ? 'control' : 'velocity';
    attributeLabel = usesControl ? 'Control' : 'Velocity';
    currentValue = attrs.pitching?.[attributeKey] ?? 3;
    const eraStr = era > 0 ? ` · ERA ${era.toFixed(2)}` : '';
    statLine = `${ip.toFixed(1)} IP · WHIP ${whip.toFixed(2)}${eraStr}`;
    reason = gap > 0
      ? `${gameCount} games of above-average pitching suggest their ${attributeLabel.toLowerCase()} rating might be selling them short.`
      : `Recent numbers show some struggles: their ${attributeLabel.toLowerCase()} rating may be worth revisiting downward.`;
  } else {
    // Batting signal: primary driver is contact (weight 0.4)
    attributeBucket = 'hitting';
    attributeKey = 'contact';
    attributeLabel = 'Contact';
    currentValue = attrs.hitting?.contact ?? 3;
    const avg = stats?.avg != null ? stats.avg.toFixed(3).replace(/^0/, '') : null;
    const ops = stats?.ops != null ? stats.ops.toFixed(3).replace(/^0/, '') : null;
    const paStr = `${pa} PA`;
    statLine = [paStr, avg ? `AVG ${avg}` : null, ops ? `OPS ${ops}` : null].filter(Boolean).join(' · ');
    reason = gap > 0
      ? `${gameCount} games of strong production suggest their contact rating might be underselling them.`
      : `Production has trended below their scouted rating over ${gameCount} games. Worth a look.`;
  }

  // Cap proposed value at 1–5 scale, nudge by exactly 1 in the direction of gap.
  const proposedValue = Math.max(1, Math.min(5, currentValue + direction));

  // If the attribute is already at the boundary in that direction, no change needed.
  if (proposedValue === currentValue) return null;

  return {
    playerId: player.id,
    playerName: player.name,
    playerNumber: player.number,
    primaryPosition: pos,
    currentOVR: scoutingOVR,
    xOVR: xovr,
    gap,
    attributeBucket,
    attributeKey,
    attributeLabel,
    currentValue,
    proposedValue,
    reason,
    statLine,
  };
}

/**
 * Top-level: scan the full roster and return all eligible proposals, sorted by gap size.
 * `games` must already be scoped to the current season.
 */
export function computeDevReviewProposals(players: any[], games: any[]): DevReviewProposal[] {
  return players
    .map(p => buildDevReviewProposal(p, games))
    .filter((p): p is DevReviewProposal => p !== null)
    .sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap));
}
